import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { catchError, finalize, of, tap } from 'rxjs'

import { BreadcrumbService } from '../../../abacus/services/breadcrumb.service'
import { FlashMessageService } from '../../../abacus/services/flash-message.service'
import {
  IncidentExport,
  IncidentExportService
} from './incident-export.service'

interface IncidentExportState {
  exports: IncidentExport[]
  isLoading: boolean
  error: string | null
  downloadingId: number | null
}

@Component({
  selector: 'app-incident-export',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="is-flex flex-mobile is-justify-content-space-between is-align-items-center mb-2"
    >
      <div class="left-part">
        <h1 class="title is-2 has-text-weight-light">
          <span class="icon is-large">
            <i class="icon icon-download is-size-2 has-text-link"></i>
          </span>
          Export AI Incidents
        </h1>
      </div>
    </div>

    <div class="card p-5 mb-6 mt-4">
      <p class="mb-4">Select an export of AI Incidents from the list below.</p>

      @if (isLoading()) {
      <div class="has-text-centered py-5">
        <span class="icon is-large">
          <i class="fas fa-spinner fa-pulse fa-2x"></i>
        </span>
        <p class="mt-2">Loading exports...</p>
      </div>
      } @else if (error()) {
      <div class="notification is-danger is-light">
        <p>{{ error() }}</p>
        <button class="button is-small mt-3" (click)="loadExports()">
          Try again
        </button>
      </div>
      } @else if (exports().length === 0) {
      <div class="notification is-info is-light">
        <p>No exports available.</p>
      </div>
      } @else {
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>Period Start</th>
            <th>Period End</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (export of exports(); track export.id) {
          <tr>
            <td>{{ export.dateStart | date : 'dd MMM yyyy' }}</td>
            <td>{{ export.dateEnd | date : 'dd MMM yyyy' }}</td>
            <td>{{ export.dateCreated | date : 'dd MMM yyyy, HH:mm' }}</td>
            <td>
              <button
                class="button is-link is-small"
                [class.is-loading]="downloadingId() === export.id"
                [disabled]="downloadingId() !== null"
                (click)="downloadExport(export)"
              >
                @if (downloadingId() !== export.id) {
                <span class="icon is-small">
                  <i class="icon icon-download"></i>
                </span>
                }
                <span>{{
                  downloadingId() === export.id ? 'Downloading...' : 'Download'
                }}</span>
              </button>
            </td>
          </tr>
          }
        </tbody>
      </table>
      }
    </div>
  `
})
export class IncidentExportComponent implements OnInit {
  private readonly incidentExportService = inject(IncidentExportService)
  private readonly breadcrumbService = inject(BreadcrumbService)
  private readonly flashMessageService = inject(FlashMessageService)
  private readonly destroyRef = inject(DestroyRef)

  private readonly state = signal<IncidentExportState>({
    exports: [],
    isLoading: true,
    error: null,
    downloadingId: null
  })

  readonly exports = computed(() => this.state().exports)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly error = computed(() => this.state().error)
  readonly downloadingId = computed(() => this.state().downloadingId)

  ngOnInit(): void {
    this.breadcrumbService.breadcrumbLinks.next([
      { label: 'Export AI Incidents' }
    ])
    this.loadExports()
  }

  loadExports(): void {
    this.state.update((s) => ({ ...s, isLoading: true, error: null }))

    this.incidentExportService
      .listExports()
      .pipe(
        tap((exports) => {
          this.state.update((s) => ({ ...s, exports }))
        }),
        catchError(() => {
          this.state.update((s) => ({
            ...s,
            error: 'Failed to load exports. Please try again.'
          }))
          return of([])
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.state.update((s) => ({ ...s, isLoading: false }))
        })
      )
      .subscribe()
  }

  downloadExport(exportItem: IncidentExport): void {
    this.state.update((s) => ({ ...s, downloadingId: exportItem.id }))

    this.incidentExportService
      .getDownloadUrl(exportItem.id)
      .pipe(
        tap(({ url }) => {
          window.open(url, '_blank')
          this.flashMessageService.success('Download started.')
        }),
        catchError(() => {
          this.flashMessageService.error(
            'Failed to download export. Please try again.'
          )
          return of(null)
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.state.update((s) => ({ ...s, downloadingId: null }))
        })
      )
      .subscribe()
  }
}
